import {readString} from 'react-papaparse';

import {MAX_RECORDS} from './constants';

type ParsedRecord = {email?: string, userId?: number};

const parseCsvContent = (fileContent: string): Promise<(string | number)[][]> => {
    return new Promise((resolve, reject) => {
        readString(fileContent, {
            header: false,
            dynamicTyping: true,
            delimiter: ',',
            skipEmptyLines: true,
            quoteChar: '"',
            complete: results => {
                if (results.errors.length > 0) {
                    reject(results.errors);
                } else {
                    resolve(results.data as (string | number)[][]);
                }
            },
        });
    });
};

const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = event => {
            if (event.target?.result) {
                resolve(event.target.result as string);
            } else {
                reject(new Error('Failed to read file.'));
            }
        };

        reader.onerror = () => {
            reject(new Error('File read error.'));
        };

        reader.readAsText(file);
    });
};

export const parseUploadListFile = async (file: File): Promise<ParsedRecord[]> => {
    const fileContent = await readFileAsText(file);
    const data = await parseCsvContent(fileContent);

    if (!data.length || data.length === 1) {
        throw new Error('The uploaded file cannot be empty.');
    }

    const headers = data[0]
        .map(cell => (cell ?? '')
            .toString()
            .toLowerCase()
            .trim());
    const emailIndex = headers.indexOf('email');
    const userIdIndex = headers.indexOf('id');

    if (emailIndex === -1 && userIdIndex === -1) {
        throw new Error('File does not contain required Email or ID columns.');
    }

    const records: ParsedRecord[] = [];

    data.slice(1).forEach(row => {
        const email = emailIndex !== -1 ? row[emailIndex]?.toString().trim() : undefined;
        const userId = userIdIndex !== -1 ? Number(row[userIdIndex]) : undefined;

        if (userId && !isNaN(userId)) {
            records.push({userId});
        } else if (email) {
            records.push({email});
        }
    });

    if (!records.length) {
        throw new Error('No valid records with Email or ID found.');
    }

    return records;
};

export const validateUploadListFile = async (file: File): Promise<boolean> => {
    if (file.type !== 'text/csv') {
        throw new Error('Invalid file type. Please upload a CSV file.');
    }

    const fileContent = await readFileAsText(file);
    const data = await parseCsvContent(fileContent);

    if (!data.length || data.length === 1) {
        throw new Error('The uploaded file cannot be empty.');
    }

    const headers = data[0].map(cell => (cell ?? '').toString().toLowerCase().trim());
    const hasRequiredColumns = headers.includes('email') || headers.includes('id');

    if (!hasRequiredColumns) {
        throw new Error('File does not contain required Email or ID columns.');
    }

    const nonEmptyRecords = data
        .slice(1)
        .filter(row => row.some(cell => cell !== null && cell !== ''));

    if (nonEmptyRecords.length > MAX_RECORDS) {
        throw new Error(`The file contains more than ${MAX_RECORDS} records.`);
    }

    return true;
};
